import { BlockTypeModel } from '../../../Common/PageBuilder/models/block-type.model';
import { userPermissionsEnum } from '../../../Core/Users/enums/user-permissions.enum';
import { ChartDataTypesEnum } from '../enums/chart-data-types.enum';
import { ChartTypesEnum, ChartTypesToChartIconsMap } from '../enums/chart-types.enum';
import * as blockTypes from '../../Reports/models/reports-block-types.model';
import { ChartSourceTypesEnum } from '../enums/chart-source-types.enum';
import { recentTopicActivityBlockType } from './BlockTypes/recent-topic.const';
import { recentTargetingActivityBlockType } from './BlockTypes/recent-targeting-activity.const';
import { recentDistributionActivityBlockType } from './BlockTypes/recent-distribution-activity.const';
import { recentReportsDistributionsBlockType } from './BlockTypes/recent-reports-distributions.const';
import { recentSurgeActivityBlockType } from './BlockTypes/recent-surge-activity.const';
import { ReportBlockTypeEnum } from '../../../Common/Reports/enums/report-block-type.enum';

export interface ChartTypeModel {
    key: ChartTypesEnum;
    icon: string;
    tooltip: string;
}
export class ChartSetupOptionModel {
    blockType: BlockTypeModel;
    // fields for the search filter
    dataTypes: ChartDataTypesEnum[];
    chartTypes: ChartTypeModel[] = [];
    chartSourceType: ChartSourceTypesEnum;
    permissions: userPermissionsEnum[];

    constructor(
        blockType: BlockTypeModel,
        dataTypes: ChartDataTypesEnum[],
        chartTypes: ChartTypesEnum[],
        chartSourceType: ChartSourceTypesEnum,
        permissions: userPermissionsEnum[]
    ) {
        this.blockType = blockType;
        this.dataTypes = dataTypes;
        const chartTypeKeys = Object.keys(ChartTypesEnum);
        this.chartTypes = chartTypes.map(chartType => {
            const chartTypeKey = chartTypeKeys.find(
                key => ChartTypesEnum[key] === chartType
            );
            return {
                key: chartType,
                icon: ChartTypesToChartIconsMap.get(chartType) || '',
                tooltip: `ChartSetupDialog.filter.${chartTypeKey}.${chartType}`
            };
        });
        this.chartSourceType = chartSourceType;
        if (permissions) {
            this.permissions = permissions;
        } else {
            this.permissions = [
                userPermissionsEnum.advancedMonitoring
            ];
        }
    }

    isGAChartType(): boolean {
        const gaChartTypes = [
            ReportBlockTypeEnum.TOP_SOCIAL_REFERRERS,
            ReportBlockTypeEnum.TOP_REFERRAL_SOURCES,
            ReportBlockTypeEnum.REFERRAL_TREND,
            ReportBlockTypeEnum.COVERAGE_IMPACT_TREND
        ];

        return gaChartTypes.includes(this.blockType.key as ReportBlockTypeEnum);
    }

    isMentionChartType(): boolean {
        const mentionChartTypes = [
            ReportBlockTypeEnum.TOP_POSTS,
            ReportBlockTypeEnum.TOP_MENTIONS
        ];

        return mentionChartTypes.includes(this.blockType.key as ReportBlockTypeEnum);
    }
};
/**
 * Data Types filter options includes Analysis:
 * "Engagments" includes: Engagements, Engagement Per Post, Engagement Rate, Applause, Conversation, Amplification
 *
 *  */
export const reportChartOptions: ChartSetupOptionModel[] = [
    // Coverage by Media Type
    new ChartSetupOptionModel(
        blockTypes.mediaTypeBreakdownBlockType,
        [
            ChartDataTypesEnum.mediaType
        ],
        [ ChartTypesEnum.pie, ChartTypesEnum.column, ChartTypesEnum.bar ],
        ChartSourceTypesEnum.single,
        [ userPermissionsEnum.advancedMonitoring ]
    ),
    // Coverage by Media Type Comparison
    new ChartSetupOptionModel(
        blockTypes.mediaTypeBreakdownComparisonBlockType,
        [
            ChartDataTypesEnum.mediaType
        ],
        [ ChartTypesEnum.column, ChartTypesEnum.bar ],
        ChartSourceTypesEnum.multi,
        [ userPermissionsEnum.advancedMonitoring ]
    ),
    // Coverage by Region
    new ChartSetupOptionModel(
        blockTypes.geographyBreakdownBlockType,
        [
            ChartDataTypesEnum.volume,
            ChartDataTypesEnum.audienceReach,
            ChartDataTypesEnum.ave,
            ChartDataTypesEnum.region
        ],
        [ ChartTypesEnum.map, ChartTypesEnum.bar, ChartTypesEnum.column ],
        ChartSourceTypesEnum.single,
        [ userPermissionsEnum.advancedMonitoring, userPermissionsEnum.socialIntelligence ]
    ),
    // Coverage Comparison
    new ChartSetupOptionModel(
        blockTypes.coverageComparisonBlockType,
        [
            ChartDataTypesEnum.volume,
            ChartDataTypesEnum.ave,
            ChartDataTypesEnum.audienceReach,
            ChartDataTypesEnum.engagements
        ],
        [ ChartTypesEnum.bar, ChartTypesEnum.column, ChartTypesEnum.pie ],
        ChartSourceTypesEnum.multi,
        [ userPermissionsEnum.advancedMonitoring, userPermissionsEnum.socialIntelligence ]
    ),
    // Coverage Impact Trend
    new ChartSetupOptionModel(
        blockTypes.coverageImpactTrendBlockType,
        [
            ChartDataTypesEnum.dataConnector
        ],

        [ ChartTypesEnum.line, ChartTypesEnum.column ],
        ChartSourceTypesEnum.single,
        [ userPermissionsEnum.advancedMonitoring, userPermissionsEnum.socialIntelligence ]
    ),
    // Coverage Metric
    new ChartSetupOptionModel(
        blockTypes.coverageMetricBlockType,
        [
            ChartDataTypesEnum.volume,
            ChartDataTypesEnum.audienceReach,
            ChartDataTypesEnum.ave,
            ChartDataTypesEnum.domainAuthority,
            ChartDataTypesEnum.sentiment,
            ChartDataTypesEnum.engagements,
            ChartDataTypesEnum.custom
        ],
        [ ChartTypesEnum.line ],
        ChartSourceTypesEnum.single,
        [ userPermissionsEnum.advancedMonitoring, userPermissionsEnum.socialIntelligence ]
    ),
    // Coverage Prominence
    new ChartSetupOptionModel(
        blockTypes.prominenceByReachBlockType,
        [
            ChartDataTypesEnum.prominence
        ],
        [ ChartTypesEnum.heat ],
        ChartSourceTypesEnum.single,
        [ userPermissionsEnum.advancedMonitoring ]
    ),
    // Coverage Prominence Trend
    new ChartSetupOptionModel(
        blockTypes.prominenceOverTimeBlockType,
        [
            ChartDataTypesEnum.prominence
        ],
        [ ChartTypesEnum.line, ChartTypesEnum.column ],
        ChartSourceTypesEnum.single,
        [ userPermissionsEnum.advancedMonitoring ]
    ),
    // Coverage Sentiment
    new ChartSetupOptionModel(
        blockTypes.sentimentBreakdownBlockType,
        [
            ChartDataTypesEnum.sentiment
        ],
        [ ChartTypesEnum.pie, ChartTypesEnum.column, ChartTypesEnum.bar ],
        ChartSourceTypesEnum.single,
        [ userPermissionsEnum.advancedMonitoring, userPermissionsEnum.socialIntelligence ]
    ),
    // Coverage Sentiment Comparison
    new ChartSetupOptionModel(
        blockTypes.sentimentBreakdownComparisonBlockType,
        [
            ChartDataTypesEnum.sentiment
        ],
        [ ChartTypesEnum.column, ChartTypesEnum.bar ],
        ChartSourceTypesEnum.multi,
        [ userPermissionsEnum.advancedMonitoring, userPermissionsEnum.socialIntelligence ]
    ),
    // Coverage Sentiment Trend
    new ChartSetupOptionModel(
        blockTypes.sentimentTrendBlockType,
        [
            ChartDataTypesEnum.sentiment
        ],
        [ ChartTypesEnum.line ],
        ChartSourceTypesEnum.single,
        [ userPermissionsEnum.advancedMonitoring, userPermissionsEnum.socialIntelligence ]
    ),
    // Coverage Summary
    new ChartSetupOptionModel(
        blockTypes.coverageSummaryBlockType,
        [
            ChartDataTypesEnum.volume,
            ChartDataTypesEnum.audienceReach,
            ChartDataTypesEnum.ave,
            ChartDataTypesEnum.sentiment,
            ChartDataTypesEnum.engagements,
        ],
        [ ChartTypesEnum.list ],
        ChartSourceTypesEnum.single,
        [ userPermissionsEnum.advancedMonitoring, userPermissionsEnum.socialIntelligence ]
    ),
    // Coverage Trend
    new ChartSetupOptionModel(
        blockTypes.coverageOverTimeBlockType,
        [
            ChartDataTypesEnum.volume,
            ChartDataTypesEnum.ave,
            ChartDataTypesEnum.audienceReach,
            ChartDataTypesEnum.engagements
        ],
        [ ChartTypesEnum.line, ChartTypesEnum.column ],
        ChartSourceTypesEnum.single,
        [ userPermissionsEnum.advancedMonitoring, userPermissionsEnum.socialIntelligence ]
    ),
    // Coverage Trend Comparison
    new ChartSetupOptionModel(
        blockTypes.coverageOverTimeComparisonBlockType,
        [
            ChartDataTypesEnum.volume,
            ChartDataTypesEnum.ave,
            ChartDataTypesEnum.audienceReach,
            ChartDataTypesEnum.engagements
        ],
        [ ChartTypesEnum.line, ChartTypesEnum.column ],
        ChartSourceTypesEnum.multi,
        [ userPermissionsEnum.advancedMonitoring, userPermissionsEnum.socialIntelligence ]
    ),
    // Referral Trend
    new ChartSetupOptionModel(
        blockTypes.referralTrendBlockType,
        [
            ChartDataTypesEnum.dataConnector
        ],

        [ ChartTypesEnum.line, ChartTypesEnum.column ],
        ChartSourceTypesEnum.single,
        [ userPermissionsEnum.advancedMonitoring, userPermissionsEnum.socialIntelligence ]
    ),
    // Share of Voice
    new ChartSetupOptionModel(
        blockTypes.shareOfVoiceBlockType,
        [
            ChartDataTypesEnum.volume,
            ChartDataTypesEnum.ave,
            ChartDataTypesEnum.audienceReach,
            ChartDataTypesEnum.engagements
        ],
        [ ChartTypesEnum.pie, ChartTypesEnum.bar, ChartTypesEnum.column ],
        ChartSourceTypesEnum.multi,
        [ userPermissionsEnum.advancedMonitoring, userPermissionsEnum.socialIntelligence ]
    ),
    // Social Coverage by Network
    new ChartSetupOptionModel(
        blockTypes.socialCoverageByNetwork,
        [
            ChartDataTypesEnum.mediaType,
            ChartDataTypesEnum.volume,
            ChartDataTypesEnum.audienceReach,
            ChartDataTypesEnum.engagements
        ],
        [ ChartTypesEnum.bar, ChartTypesEnum.column, ChartTypesEnum.pie ],
        ChartSourceTypesEnum.single,
        [ userPermissionsEnum.socialIntelligence ]
    ),
    // Top 10 Authors
    new ChartSetupOptionModel(
        blockTypes.authorsBreakdownBlockType,
        [
            ChartDataTypesEnum.topSources,
            ChartDataTypesEnum.volume,
            ChartDataTypesEnum.audienceReach,
            ChartDataTypesEnum.ave
        ],
        [ ChartTypesEnum.bar, ChartTypesEnum.column ],
        ChartSourceTypesEnum.single,
        [ userPermissionsEnum.advancedMonitoring, userPermissionsEnum.socialIntelligence ]
    ),
    // Top 10 Outlets
    new ChartSetupOptionModel(
        blockTypes.outletsBreakdownBlockType,
        [
            ChartDataTypesEnum.topSources,
            ChartDataTypesEnum.volume,
            ChartDataTypesEnum.audienceReach,
            ChartDataTypesEnum.ave,
            ChartDataTypesEnum.domainAuthority
        ],
        [ ChartTypesEnum.bar, ChartTypesEnum.column ],
        ChartSourceTypesEnum.single,
        [ userPermissionsEnum.advancedMonitoring ]
    ),
    // Top Hashtags
    new ChartSetupOptionModel(
        blockTypes.topHashtagsBlockType,
        [
            ChartDataTypesEnum.topSources
        ],
        [ ChartTypesEnum.bar, ChartTypesEnum.column ],
        ChartSourceTypesEnum.single,
        [ userPermissionsEnum.advancedMonitoring, userPermissionsEnum.socialIntelligence ]
    ),
    // Top Mentions
    new ChartSetupOptionModel(
        blockTypes.topMentionsBlockType,
        [
            ChartDataTypesEnum.topSources,
            ChartDataTypesEnum.audienceReach,
            ChartDataTypesEnum.ave,
            ChartDataTypesEnum.domainAuthority,
            ChartDataTypesEnum.custom
        ],
        [ ChartTypesEnum.list ],
        ChartSourceTypesEnum.single,
        [ userPermissionsEnum.advancedMonitoring, userPermissionsEnum.socialIntelligence ]
    ),
    // Top Posts
    new ChartSetupOptionModel(
        blockTypes.topPostsBlockType,
        [
            ChartDataTypesEnum.topSources,
            ChartDataTypesEnum.audienceReach,
            ChartDataTypesEnum.engagements,
            ChartDataTypesEnum.custom
        ],
        [ ChartTypesEnum.list ],
        ChartSourceTypesEnum.single,
        [ userPermissionsEnum.advancedMonitoring, userPermissionsEnum.socialIntelligence ]
    ),
    // Top Referral Sources
    new ChartSetupOptionModel(
        blockTypes.topReferralSourcesBlockType,
        [
            ChartDataTypesEnum.dataConnector
        ],

        [ ChartTypesEnum.bar, ChartTypesEnum.column ],
        ChartSourceTypesEnum.single,
        [ userPermissionsEnum.advancedMonitoring, userPermissionsEnum.socialIntelligence ]
    ),
    // Top Social Referrers
    new ChartSetupOptionModel(
        blockTypes.topSocialReferrersBlockType,
        [
            ChartDataTypesEnum.dataConnector
        ],

        [ ChartTypesEnum.bar, ChartTypesEnum.column ],
        ChartSourceTypesEnum.single,
        [ userPermissionsEnum.advancedMonitoring, userPermissionsEnum.socialIntelligence ]
    ),
    // Top Terms
    new ChartSetupOptionModel(
        blockTypes.topTermsBlockType,
        [
            ChartDataTypesEnum.topSources,
            ChartDataTypesEnum.volume,
            ChartDataTypesEnum.ave,
            ChartDataTypesEnum.audienceReach,
            ChartDataTypesEnum.engagements
        ],
        [ ChartTypesEnum.list, ChartTypesEnum.wordCloud ],
        ChartSourceTypesEnum.single,
        [ userPermissionsEnum.advancedMonitoring, userPermissionsEnum.socialIntelligence ]
    ),
];

export const dashboardChartOptions: ChartSetupOptionModel[] = [
    // Recent Activity - Distributions
    new ChartSetupOptionModel(
        recentDistributionActivityBlockType,
        [
            ChartDataTypesEnum.recentActivity
        ],
        [ ChartTypesEnum.list ],
        ChartSourceTypesEnum.single,
        [
            userPermissionsEnum.advancedTargeting
        ]
    ),
    // Recent Activity - Topics
    new ChartSetupOptionModel(
        recentTopicActivityBlockType,
        [
            ChartDataTypesEnum.recentActivity
        ],
        [ ChartTypesEnum.line ],
        ChartSourceTypesEnum.multi,
        [
            userPermissionsEnum.advancedMonitoring
        ]
    ),
    // Recent Activity - Media Relations
    new ChartSetupOptionModel(
        recentTargetingActivityBlockType,
        [
            ChartDataTypesEnum.recentActivity
        ],
        [ ChartTypesEnum.list ],
        ChartSourceTypesEnum.single,
        [
            userPermissionsEnum.advancedTargeting
        ]
    ),
    // Recent Reports - Distributions
    new ChartSetupOptionModel(
        recentReportsDistributionsBlockType,
        [
            ChartDataTypesEnum.recentActivity
        ],
        [ ChartTypesEnum.list ],
        ChartSourceTypesEnum.single,
        [
            userPermissionsEnum.advancedTargeting
        ]
    ),
    // Recent Surge Alerts
    new ChartSetupOptionModel(
        recentSurgeActivityBlockType,
        [
            ChartDataTypesEnum.recentActivity
        ],
        [ ChartTypesEnum.list ],
        ChartSourceTypesEnum.single,
        [
            userPermissionsEnum.advancedMonitoring
        ]
    ),
];
